import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor } from "@theme/utilities";

/**
 * Site header: keeps `--header-height` in sync, optionally updates meta theme-color
 * when `data-theme-color` is set, and toggles `data-header-hidden` on scroll down / up.
 *
 * @extends {Component}
 */
class HeaderComponent extends Component {
  /**
   * Hide the bar only after this much document scroll — avoids a visible gap before it slides away.
   * @constant {number}
   */
  #hideAfterScrollPx = 120;

  /** @type {IntersectionObserver | null} */
  #metaThemeObserver = null;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {VisualViewport | null} */
  #visualViewport = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const headerHeight = entry.borderBoxSize[0].blockSize;
    document.body.style.setProperty("--header-height", `${headerHeight}px`);
  });

  /**
   * Viewport scroll position only. Do not Math.max() across html/body — a stale body.scrollTop
   * can stay high and block detecting scroll-up.
   */
  #getScrollTop = () => window.scrollY ?? window.pageYOffset ?? 0;

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateHeaderHidden();
    });
  };

  /**
   * Wheel/trackpad up (negative deltaY) reveals the bar when past the hide threshold — helps when
   * `scroll-behavior: smooth` makes scroll position updates sparse between frames.
   */
  #handleWheel = (event) => {
    if (event.deltaY >= 0) return;
    if (this.#getScrollTop() < this.#hideAfterScrollPx) return;
    this.removeAttribute("data-header-hidden");
  };

  /**
   * Hide after scrolling down past #hideAfterScrollPx; show on any scroll up or when above that offset.
   * If scroll position is unchanged between events, keep current visibility (avoids flicker).
   */
  #updateHeaderHidden = () => {
    const y = this.#getScrollTop();
    const last = this.#lastScrollTop;

    if (y < last || y < this.#hideAfterScrollPx) {
      this.removeAttribute("data-header-hidden");
    } else if (y > last && y >= this.#hideAfterScrollPx) {
      this.setAttribute("data-header-hidden", "");
    }

    this.#lastScrollTop = y;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    if (this.dataset.themeColor && !this.#metaThemeObserver) {
      this.#metaThemeObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          changeMetaThemeColor(this.dataset.themeColor);
        },
        { threshold: 1 }
      );
      this.#metaThemeObserver.observe(this);
    }

    this.#lastScrollTop = this.#getScrollTop();
    window.addEventListener("scroll", this.#handleScroll, { passive: true });
    this.#visualViewport = window.visualViewport ?? null;
    this.#visualViewport?.addEventListener("scroll", this.#handleScroll, { passive: true });
    window.addEventListener("wheel", this.#handleWheel, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#metaThemeObserver?.disconnect();
    this.#metaThemeObserver = null;
    window.removeEventListener("scroll", this.#handleScroll);
    this.#visualViewport?.removeEventListener("scroll", this.#handleScroll);
    this.#visualViewport = null;
    window.removeEventListener("wheel", this.#handleWheel);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute("transparent") && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty("--header-group-height", `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          const nextChildren = headerGroup.children;
          for (let j = 0; j < nextChildren.length; j++) {
            const element = nextChildren[j];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
